// @ts-nocheck
import queryString from 'query-string';
import axios, { AxiosInstance } from 'axios';
import * as retryAxios from 'retry-axios';

import { normalizeData } from '../helpers/normalize-data';
import msalService from '../services/msalService';
import { getLabelSupportData } from '../helpers/utils';
import { jobsGetParams, solverOutputGenerateId } from '../helpers/data';
import {
  AggregatedJobsParams,
  AggregatedPogMetricsParams,
  AggregatedProductMetricsParams,
  AggregatedSolverOutputParams,
  AutoSavedPogSolverOutputParams,
  DeleteNotificationBannerParams,
  FlipPogParams,
  GetAssortmentParams,
  GetAssortmentSearchParams,
  GetAvailableAislesParams,
  GetDefaultPogMetricsParams,
  GetDefaultProductMetricsParams,
  GetEditedJobParams,
  getFetchCombinedPogParams,
  getFetchPogByStoreParams,
  GetImagesParams,
  GetMatchedPegboardConfigParams,
  getOptimizationJobParams,
  getPogMetricsParams,
  GetProductMetricsParams,
  GetOptimizationStatusParams,
  GetParametersParams,
  PogSolverOutputParams,
  LaunchOptimizationJobParams,
  OptimalSpaceSegmentParams,
  ProtoLinearParams,
  CopySharedPogParams,
  PushPogToStoreParams,
  SaveDragAndDropEditsParams,
  SaveMyNotesParams,
  SaveNewEditedPogParams,
  SaveNewNotificationBannerParams,
  SaveUserPreferenceParams,
  SetUserRoleParams,
  UpdateNotificationBannerParams,
  SeedsParams,
  GetAssortmentProductParams,
  UpdateSeedSharedParams,
  ApproveSeedParams,
  RemovePogParams,
  GetMerchGuideLinkParams,
  ChangeSharedPogNameParams,
  ChangePogNameParams,
  ChangePrivateAndSharedPogNamesParams,
  UpdateSeedOwnershipParams,
} from '../types/requests';
import {
  AggregatedJobsResponse,
  AggregatedPogMetricsResponse,
  AggregatedProductMetricsResponse,
  AggregatedSolverOutput,
  AssortmentApiResponse,
  AuthResponse,
  AutosaveRespnse,
  DatasetInfo,
  DeleteNotificationBannerResponse,
  EditedJobResponse,
  FlipPogResponse,
  GetAllNotificationBannersResponse,
  GetAssortmentOnlyResponse,
  GetAssortmentSearchResponse,
  GetAvailableAislesResponse,
  GetFetchCombinedPogResponse,
  GetFetchPogByStoreResponse,
  GetImagesResponse,
  GetMatchedPegboardConfigResponse,
  GetProductMetricsResponse,
  GetOptimalSpaceSegmentResponse,
  GetOptimizationStatusResponse,
  GetParametersResponse,
  GetDefaultProductMetricsResponse,
  GetDefaultPogMetricsResponse,
  GetPogMetricsResponse,
  GetUserPreferenceResponse,
  LaunchOptimizationJobResponse,
  OptimizationJobsResponse,
  ProtoLinearResponse,
  CopySharedPogResponse,
  PushPogToStoreResponse,
  SaveDragAndDropEditsResponse,
  SaveMyNotesResponse,
  SaveNewEditedPogResponse,
  SaveNewNotificationBannerResponse,
  SaveUserPreferenceResponse,
  setResizedPogResponse,
  PogSolverOutputResponse,
  UpdateNotificationBannerResponse,
  AssortmentBulkSearchResponse,
  SeedsResponse,
  MerchGuideLinkResponse,
} from '../types/responses';
import type { GetFixturesNotchesHolesResponse, GetFixturesResponse } from '../state/fixtures/fixtures.types';
import { APIMSUBSCRIPTION_DEFAULT_VALUE } from '../utils/constants';
import { getConfig } from '../utils/getConfig';
import { VendorPegbordAPIItem } from '../state/vendorPegboard/vendorPegboard.types';
import { reqBody } from '../components/ProtoPog/Provider/protopog.types';
import { ReleaseNotesType } from '../pages/HomePage/Provider/HomePage.types';
import { API } from './apiClient.constants';
import { JobDeleteParams, JobType } from '../state/job/job.types';
import { SetResizedPogRequestParams } from '../state/viewAndEdit/viewAndEdit.types';
import { formatDateToYYYYMMDD } from '../helpers/data';

const REQUEST_TIMEOUT = 60000;
const APIMSUBSCRIPTION = getConfig('APIMSUBSCRIPTION') || APIMSUBSCRIPTION_DEFAULT_VALUE;

export function createAxiosInstance(apiUrl: string): AxiosInstance {
  const axiosInstance = axios.create({
    baseURL: apiUrl,
    timeout: REQUEST_TIMEOUT,
    withCredentials: true,
    headers: { 'Ocp-Apim-Subscription-Key': APIMSUBSCRIPTION }
  });

  // DELETE is omitted deliberately: the backend deletes before it can fail, so replaying a 5xx
  // reports "not found" for a record the first attempt already removed.
  axiosInstance.defaults.raxConfig = {
    instance: axiosInstance,
    httpMethodsToRetry: ['GET', 'HEAD', 'OPTIONS', 'PUT'],
  };

  retryAxios.attach(axiosInstance);

  return axiosInstance;
}

export class ApiClient {
  axiosInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  setAccessToken(accessToken: string) {
    this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }

  async msalHandleAccessToken() {
    try {
      const accessToken = await msalService.checkAccessToken();

      if (accessToken) {
        this.setAccessToken(accessToken);
      }
    } catch (e) {
      console.log('msalHandleAccessToken error', e);
    }
  }

  /* istanbul ignore next */
  async requestHandlerGet(url: string, data = {}) {
    try {
      await this.msalHandleAccessToken();

      return await this.axiosInstance.get(url, data);
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          await msalService.logout();
        }
        // TODO: should investigate how to add handler for 403 status with error message "Forbidden" in future
      }

      throw error;
    }
  }

  async requestHandlerPost(url: string, data = {}) {
    try {
      await this.msalHandleAccessToken();

      return await this.axiosInstance.post(url, data);
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          await msalService.logout();
        }
      }

      throw error;
    }
  }

  async requestHandlerDelete(url: string, data = {}) {
    try {
      await this.msalHandleAccessToken();

      return await this.axiosInstance.delete(url, data);
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          await msalService.logout();
        }
      }

      throw error;
    }
  }

  async getAuth(): Promise<AuthResponse> {
    const response = await this.requestHandlerGet(API.getAuth);

    return response.data.responseData;
  }

  async setUserRole({ username, new_roles }: SetUserRoleParams): Promise<void> {
    const response = await this.requestHandlerPost(API.getUserRole, { username, new_roles });

    return response.data;
  }

  async getAvailableAisles(
    params: GetAvailableAislesParams
  ): Promise<GetAvailableAislesResponse> {
    const { storeNumber } = params;

    const url = API.getAvailableAisles(storeNumber);
    const response = await this.requestHandlerGet(url);
    let { responseData } = response.data;

    responseData = responseData.map((r) => {
      const labelSupportValue = getLabelSupportData(r);

      r.geometry = r.aisle_geometry;
      r.status = labelSupportValue.color;
      r.label_support = labelSupportValue.value;

      return r;
    });

    return responseData;
  }

  async getParameters(
    params: GetParametersParams
  ): Promise<GetParametersResponse> {
    const { aisleCode, storeNumber } = params;
    const url = API.getParameters(storeNumber, aisleCode);
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async getAssortmentOnly(
    params: GetAssortmentParams
  ): Promise<GetAssortmentOnlyResponse> {
    try {
      const { aisleCode, storeNumber, jobId = '' } = params;
      const query = jobId ? `?${queryString.stringify({ job_id: jobId })}` : '';
      const urlAssortment = API.getAssortmentOnly(storeNumber, aisleCode, query);

      const responseAssortment = await this.requestHandlerGet(urlAssortment);

      return { assortmentApi: responseAssortment.data.responseData };
    } catch (err) {
      console.log(err);
    }
  }

  async getDefaultPogMetrics(
    params: GetDefaultPogMetricsParams
  ): Promise<GetDefaultPogMetricsResponse> {
    const { storeNumber, aisleCode, demand, use_iss, use_prom } = params;
    const query = demand ? `?demand=${demand}&use_iss=${use_iss}&use_prom=${use_prom}` : '';
    const url = API.getDefaultPogMetrics(storeNumber, aisleCode, query);

    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async getPogMetrics(
    params: getPogMetricsParams
  ): Promise<GetPogMetricsResponse> {
    const { storeNumber, aisleCode, jobId } = params;
    const url = API.getPogMetrics(storeNumber, aisleCode, jobId);
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async pushPogToStore(params: PushPogToStoreParams): Promise<PushPogToStoreResponse> {
    const url = API.pushPogToStore;
    const response = await this.requestHandlerPost(url, params);

    return response.data.responseData;
  }

  async copySharedPog(params: CopySharedPogParams): Promise<CopySharedPogResponse> {
    const url = API.copySharedPog;
    const response = await this.requestHandlerPost(url, params);

    return response.data.responseData;
  }

  async getDefaultProductMetrics(
    params: GetDefaultProductMetricsParams
  ): Promise<GetDefaultProductMetricsResponse> {
    const { storeNumber, aisleCode, demand, use_iss, use_prom } = params;
    const query = demand ? `?demand=${demand}&use_iss=${use_iss}&use_prom=${use_prom}` : '';
    const url = API.getDefaultProductMetrics(storeNumber, aisleCode, query);
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async launchOptimizationJob(
    params: LaunchOptimizationJobParams
  ): Promise<LaunchOptimizationJobResponse> {
    const url = API.launchOptimizationJob;
    const response = await this.requestHandlerPost(url, params);

    return response.data.responseData;
  }

  async getOptimizationStatus(
    params: GetOptimizationStatusParams
  ): Promise<GetOptimizationStatusResponse> {
    const url = API.getOptimizationStatus(params.jobId);
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async getPogSolverOutput(
    params: PogSolverOutputParams
  ): Promise<PogSolverOutputResponse> {
    const { storeNumber, aisleCode, jobId } = params;
    const url = API.getPogSolverOutput(storeNumber, aisleCode, jobId);
    const response = await this.requestHandlerGet(url);

    return solverOutputGenerateId(response.data.responseData);
  }

  async getProductMetrics(
    params: GetProductMetricsParams
  ): Promise<GetProductMetricsResponse> {
    const { storeNumber, aisleCode, jobId } = params;
    const url = API.getProductMetrics(storeNumber, aisleCode, jobId);
    const response = await this.requestHandlerGet(url);

    return { ...response.data.responseData, jobId };
  }
  /* istanbul ignore next */
  async getOptimizationJobLimit(params: getOptimizationJobParams): Promise<any> {
    const { storeNumber, aisleId, limit = 10 } = params;
    let optimizedResponse = [];
    let editedResponse = [];
    const url = API.getOptimizationJobLimit(storeNumber, aisleId, limit);
    const response = await this.requestHandlerGet(url);

    optimizedResponse =
      Array.isArray(response.data.responseData) &&
      response.data.responseData?.map((job) => ({ ...job, type: JobType.optimized }));
    const jobIds =
      Array.isArray(optimizedResponse) &&
      optimizedResponse?.map((job) => job.job_id);

    if (jobIds.length) {
      editedResponse = await this.getEditedJobs({ storeNumber, aisleCode: aisleId, jobIds });
      editedResponse = editedResponse.map(job => ({ ...job, type: JobType.edited }));
    }

    return optimizedResponse && optimizedResponse.concat(editedResponse);
  }

  async getLatestOptimizationJob(params: getOptimizationJobParams): Promise<OptimizationJobsResponse> {
    const { storeNumber } = params;
    const url = API.getLatestOptimizationJob(storeNumber);
    const response = await this.requestHandlerGet(url);

    return response.data;
  }

  async serverSideLogout(): Promise<void> {
    const url = API.serverSideLogout;

    return await this.axiosInstance.post(url);
  }

  //TODO we might need this code in the future
  async saveDragAndDropEdits({
    id,
    params,
  }: {
    id: string;
    params: SaveDragAndDropEditsParams;
  }): Promise<SaveDragAndDropEditsResponse> {
    const url = API.saveDragAndDropEdits(id);
    const response = await this.requestHandlerPost(url, params);

    return response.data;
  }
  //TODO we might need this code in the future

  async getImages(
    params: GetImagesParams
  ): Promise<GetImagesResponse> {
    const { images } = params;
    const imagesUrlParams = queryString.stringify({ sku: images }, { arrayFormat: 'bracket' });
    const url = `${API.getImages}?${imagesUrlParams}`;
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async getDatasetInfo(
  ): Promise<DatasetInfo> {
    const url = API.getDatasetInfo;
    const response = await this.requestHandlerGet(url);
    const { responseData } = response.data;
    const date = responseData.dataset_info.inv_date.split('-');

    responseData.dataset_info.inv_date = `${date[2]}/${date[1]}/${date[0]}`;

    return responseData;
  }

  async getOptimalSpaceSegment(
    params: OptimalSpaceSegmentParams
  ): Promise<GetOptimalSpaceSegmentResponse> {
    const { storeNumber, aisleCode, reqBody } = params;
    const url = API.getOptimalSpaceSegment(storeNumber, aisleCode);
    const response = await this.requestHandlerPost(url, reqBody);
    const { responseData } = response.data;

    return responseData;
  }

  async getEditedJobs(params: GetEditedJobParams): Promise<EditedJobResponse> {
    const { storeNumber, aisleCode, jobIds } = params;
    const jobsUrlParams = queryString.stringify({ job_ids: jobIds }, { arrayFormat: 'bracket' });
    const url = `${API.getEditedJobs(storeNumber, aisleCode)}?${jobsUrlParams}`;
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async saveNewEditedPog(
    params: SaveNewEditedPogParams
  ): Promise<SaveNewEditedPogResponse> {
    const { storeNumber, aisleCode, reqBody } = params;
    const url = API.saveNewEditedPog(storeNumber, aisleCode);
    const response = await this.requestHandlerPost(url, reqBody);
    const { responseData } = response.data;

    return responseData;
  }

  async updateEditedPog(
    params: SaveNewEditedPogParams
  ): Promise<SaveNewEditedPogResponse> {
    const { storeNumber, aisleCode, reqBody } = params;
    const url = API.updateEditedPog(storeNumber, aisleCode);
    const response = await this.requestHandlerPost(url, reqBody);
    const { responseData } = response.data;

    return responseData;
  }

  async autoSavePog(
    params: SaveNewEditedPogParams
  ): Promise<AutosaveRespnse> {
    try {
      const { storeNumber, aisleCode, reqBody } = params;
      const url = API.autoSavePog(storeNumber, aisleCode);
      const response = await this.requestHandlerPost(url, reqBody);
      const { responseData } = response.data;

      if (responseData) {
        return true;
      }
    } catch (e) {
      return false;
    }
  }

  async autoSavedPogSolverOutput(
    params: AutoSavedPogSolverOutputParams
  ): Promise<PogSolverOutputResponse> {
    try {
      const { storeNumber, aisleCode, jobId } = params;
      const url = API.autoSavedPogSolverOutput(storeNumber, aisleCode, jobId);
      const response = await this.requestHandlerGet(url);
      const solverOutput = solverOutputGenerateId(response.data.responseData);

      return solverOutput;
    } catch (error) {
      return null;
    }
  }

  async getFetchPogByStore(params: getFetchPogByStoreParams): Promise<GetFetchPogByStoreResponse[]> {
    const { storeNumber } = params;
    const url = API.getFetchPogByStore(storeNumber);
    const response = await this.requestHandlerGet(url);
    const { responseData } = response.data;

    Array.isArray(responseData) && /* istanbul ignore next */responseData?.forEach(item => {
      item.aisle = item.main_aisle_num !== null ?
        `${item.main_aisle_orientation}${item.main_aisle_num}` :
        null;
      item.aisleForSorting = item.main_aisle_num !== null ?
        `${item.main_aisle_num}${item.main_aisle_orientation}` :
        null;
      item.optimizedDate = item.updated_at ? formatDateToYYYYMMDD(item.updated_at) : null;
      item.linear = item.linear_feet;
      item.height = item.max_of_height;
      item.depth = item.max_of_depth;
    });

    return responseData;
  }

  async getFetchCombinedPog(params: getFetchCombinedPogParams): Promise<GetFetchCombinedPogResponse[]> {
    const { storeNumber } = params;
    const url = API.getFetchCombinedPog(storeNumber);
    const response = await this.requestHandlerGet(url);
    const { responseData } = response.data;

    Array.isArray(responseData) && /* istanbul ignore next */responseData?.forEach(item => {
      const toTitleCase = (value: string) => value.toLowerCase().replace(/\w+/g, (word) => (
        word.charAt(0).toUpperCase() + word.slice(1)
      ));

      // @TODO: Remove this once backend pass store type for all records
      item.StoreType = item.StoreType ?? 'CTR';

      item.aisle = item.main_aisle_num !== null ?
        `${item.main_aisle_orientation || ''}${item.main_aisle_num}` :
        null;
      item.aisleForSorting = item.main_aisle_num !== null ?
        `${item.main_aisle_num}${item.main_aisle_orientation || ''}` :
        null;
      item.optimizedDate = item.updated_at ? formatDateToYYYYMMDD(item.updated_at) : null;
      const categorySource = item.aisle_category || item.EnglishPOGTitle || '';

      item.category = categorySource ? toTitleCase(categorySource) : '';
      item.code = item.aisle_code;
      item.linear = item.linear_feet;
      item.height = item.max_of_height;
      item.depth = item.max_of_depth;
      item.pogNumber = item.PogNumberWoSize;
      const statusFromLabel = item.label_status ? getLabelSupportData(item).color : '';
      const statusSource = statusFromLabel || item.status || item.Status || item.confidenceLevel || item.confidence_level || '';

      item.status = typeof statusSource === 'string' ? statusSource.toUpperCase() : '';
      item.label_support = item.label_status ? getLabelSupportData(item).value : '';
    });

    return responseData;
  }

  async getAllNotificationBanners(params): Promise<GetAllNotificationBannersResponse> {
    const url = `${API.getAllNotificationBanners}?isExpiredNotification=${params}`;
    const response = await this.requestHandlerGet(url);
    const responseWithRemovedAudit =
        Array.isArray(response.data.responseData) &&
        /* istanbul ignore next */response.data.responseData?.map((el) => {
          const { audit, ...rest } = el;

          return rest;
        });

    return responseWithRemovedAudit;
  }

  async getProtoPogs(reqBody: reqBody) {
    const response = await this.requestHandlerPost(API.getProtoPogs, reqBody);

    return response.data.responseData.matched_proto_pog;
  }

  async getReleaseNotes(language: string): Promise<ReleaseNotesType[]> {
    const response = await this.requestHandlerPost(API.getReleaseNotes, { language });

    return response?.data?.responseData;
  }

  async getWhatsNew(language: string) {
    const response = await this.requestHandlerPost(API.getWhatsNew, { language });

    return response?.data?.responseData;
  }

  async updateNotificationBanner(params: UpdateNotificationBannerParams): Promise<UpdateNotificationBannerResponse> {
    const { notificationId, reqBody } = params;
    const url = API.updateNotificationBanner(notificationId);
    const response = await this.requestHandlerPost(url, reqBody);

    return response.data.responseData;
  }

  async saveNewNotificationBanner(params: SaveNewNotificationBannerParams): Promise<SaveNewNotificationBannerResponse> {
    const { reqBody } = params;
    const url = API.saveNewNotificationBanner;
    const response = await this.requestHandlerPost(url, reqBody);

    return response.data.responseData;
  }

  async deleteNotificationBanner(params: DeleteNotificationBannerParams): Promise<DeleteNotificationBannerResponse> {
    const { notificationId } = params;
    const url = API.deleteNotificationBanner(notificationId);
    const response = await this.requestHandlerDelete(url);

    return response.data.responseData;
  }

  async aggregatedJobs(params: AggregatedJobsParams): Promise<AggregatedJobsResponse> {
    const { storeNumber, aisleId, limit = 10, jobIds } = params;
    const query = jobIds ? `?${queryString.stringify({ job_ids: jobIds }, { arrayFormat: 'comma' })}` : '';
    const url = API.aggregatedJobs(storeNumber, aisleId, limit, query);
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async aggregatedSolverOutput(
    params: AggregatedSolverOutputParams
  ): Promise<AggregatedSolverOutput> {
    const { storeNumber, aisleId, jobs } = params;
    const jobsParams = jobsGetParams(jobs);
    const url = `${API.aggregatedSolverOutput(storeNumber, aisleId)}?${jobsParams}`;
    const response = await this.requestHandlerGet(url);
    const solverOutput = Array.isArray(response.data.responseData) && /* istanbul ignore next */response?.data.responseData.map(solverOutput => solverOutputGenerateId(solverOutput));

    return solverOutput;
  }

  async aggregatedProductMetrics(
    params: AggregatedProductMetricsParams
  ): Promise<AggregatedProductMetricsResponse> {
    const { storeNumber, aisleCode, jobs } = params;
    const jobsParams = jobsGetParams(jobs);
    const url = `${API.aggregatedProductMetrics(storeNumber, aisleCode)}?${jobsParams}`;

    const response = await this.requestHandlerGet(url);
    const [jobMetricMap] = normalizeData(response.data.responseData, 'job_id') || [];

    return jobMetricMap;
  }

  async aggregatedPogMetrics(
    params: AggregatedPogMetricsParams
  ): Promise<AggregatedPogMetricsResponse> {
    const { storeNumber, aisleCode, jobs } = params;
    const jobsParams = jobsGetParams(jobs);
    const url = `${API.aggregatedPogMetrics(storeNumber, aisleCode)}?${jobsParams}`;

    const response = await this.requestHandlerGet(url);
    const [jobMetricMap] = normalizeData(response.data.responseData, 'job_id') || [];

    return jobMetricMap;
  }

  async getAssortmentProductData(
    { storeNumber, aisleCode, product_nums, jobId = '' }: GetAssortmentProductParams
  ): Promise<AssortmentApiResponse> {
    try {
      let response;
      const query = jobId ? `?${queryString.stringify({ job_id: jobId })}` : '';
      const url = API.getAssortmentProductData(storeNumber, aisleCode, query);

      if (product_nums) {
        response = await this.requestHandlerPost(url, product_nums);
      } else {
        response = await this.requestHandlerGet(url);
      }

      const { packagingInfo, productInfo, blockingAttributesInfo = {}} = response.data.responseData;
      const [packaging] = normalizeData(packagingInfo, 'product_num') || [];
      const [products, productsList] = normalizeData(productInfo, 'product_num') || [];
      const { names: blockingList, values: blocking, values_fr: blockingFr } = blockingAttributesInfo;

      return {
        ...response.data.responseData,
        packagingInfo: packaging,
        productInfo: { products, productsList },
        productInfoNotNormalized: productInfo,
        blockingAttributesInfo: { blockingList, blocking, blockingFr },
      };
    } catch (err) {
      console.error(err);
    }
  }

  async saveUserPreference(params: SaveUserPreferenceParams): Promise<SaveUserPreferenceResponse> {
    const { reqBody } = params;
    const url = API.saveUserPreference;
    const response = await this.requestHandlerPost(url, reqBody);

    return response.data.responseData;
  }

  async getUserPreference(): Promise<GetUserPreferenceResponse> {
    const response = await this.requestHandlerGet(API.getUserPreference);
    const data = response.data.responseData;

    if (!data.length) {
      return {
        'language': 'en',
        'workflowTooltips': null,
        'preAssortment': {},
        'compare': null,
        'postAssortment': {},
        'labels': {},
        'pogDashboard': null,
        'pogDashboardStatus': null,
      };
    }

    const pref = data[0];
    const { preAssortment, postAssortment } = pref;

    return {
      ...pref,
      preAssortment: preAssortment === null ? {} : preAssortment,
      postAssortment: postAssortment === null ? {} : postAssortment,
    };
  }

  async saveMyNotes(
    params: SaveMyNotesParams
  ): Promise<SaveMyNotesResponse> {
    const url = API.saveMyNotes;
    const response = await this.requestHandlerPost(url, params);
    const { responseData } = response.data;

    return responseData;
  }

  async getProtoLinear(params: ProtoLinearParams): Promise<ProtoLinearResponse> {
    const { aisleCode, label, orientation } = params;
    const url = `${API.getProtoLinear(aisleCode)}?side=${orientation}&labels=${label}`;
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async flipPog(
    params: FlipPogParams
  ): Promise<FlipPogResponse> {
    const { storeNumber, aisleCode, job_id, is_edited } = params;
    const url = API.flipPog(storeNumber, aisleCode);
    const response = await this.requestHandlerPost(url, { job_id, is_edited });
    const { responseData } = response.data;

    return { ...responseData, solver_output: solverOutputGenerateId(responseData.solver_output) };
  }

  async getVendorPegboards(): Promise<{
    [key: string]: Array<VendorPegbordAPIItem>
  }> {
    const url = API.getVendorPegboards;
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async getMatchedPegboardConfig(
    params: GetMatchedPegboardConfigParams
  ): Promise<GetMatchedPegboardConfigResponse> {
    const { vendor, start_hole_x, start_hole_y, reqBody } = params;
    const url = `${API.getMatchedPegboardConfig}?vendor=${vendor}&start_hole_x=${start_hole_x}&start_hole_y=${start_hole_y}`;
    const response = await this.requestHandlerPost(url, reqBody);

    return response.data.responseData;
  }

  async getFixtures(): Promise<GetFixturesResponse> {
    const url = API.getFixtures;
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async getFixturesNotchesHoles(): Promise<GetFixturesNotchesHolesResponse> {
    const url = API.getFixturesNotchesHoles;
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async assortmentSearch(
    params: GetAssortmentSearchParams
  ): Promise<GetAssortmentSearchResponse> {
    const { storeId, searchString } = params;
    const url = API.assortmentSearch(storeId, searchString);
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async assortmentBulkSearch(
    params: { storeId: string; skuList: string[] }
  ): Promise<AssortmentBulkSearchResponse> {
    const { storeId, skuList } = params;
    const url = API.assortmentBulkSearch(storeId);
    const body = { skus: skuList };
    const response = await this.requestHandlerPost(url, body);

    return response.data.responseData;
  }

  async getAssortmentFullInfo(params: { storeId: string; aisleCode: string; skuList: string[] }): Promise<AssortmentApiResponse> {
    const { storeId, aisleCode, skuList } = params;
    const url = API.getAssortmentFullInfo(storeId, aisleCode);
    const body = { product_nums: skuList };
    const response = await this.requestHandlerPost(url, body);

    return response.data.responseData;
  }

  async setResizedPog(reqBody: SetResizedPogRequestParams): Promise<setResizedPogResponse> {
    const url = API.setResizedPog;

    const response = await this.requestHandlerPost(url, reqBody);

    return response.data.responseData;
  }

  async deletePogJob(reqBody: JobDeleteParams): Promise<void> {
    const url = API.deletePogJob;
    const response = await this.requestHandlerPost(url, reqBody);

    return response.data.responseData;
  }

  async removePog(reqBody: RemovePogParams): Promise<void> {
    const url = API.removePog;
    const response = await this.requestHandlerDelete(url, { data: reqBody });

    return response.data.responseData;
  }

  async seeds(params: SeedsParams): Promise<SeedsResponse> {
    const { storeNumber, aisleCode, reqBody } = params;
    const url = API.seeds(storeNumber, aisleCode);
    const response = await this.requestHandlerPost(url, reqBody);

    return response.data.responseData;
  }

  async updateSeedShared(reqBody: UpdateSeedSharedParams): Promise<void> {
    const response = await this.requestHandlerPost(API.updateSeedShared, reqBody);

    return response.data.responseData;
  }

  async approveSeed(reqBody: ApproveSeedParams): Promise<void> {
    const response = await this.requestHandlerPost(API.approveSeed, reqBody);

    return response.data.responseData;
  }

  async getMerchGuideLink(params: GetMerchGuideLinkParams): Promise<MerchGuideLinkResponse> {
    const { storeNumber, aisleCode } = params;
    const url = API.getMerchGuideLink(storeNumber, aisleCode);
    const response = await this.requestHandlerGet(url);

    return response.data.responseData;
  }

  async changeSharedPogName(reqBody: ChangeSharedPogNameParams): Promise<void> {
    const response = await this.requestHandlerPost(API.changeSharedPogName, reqBody);

    return response.data.responseData;
  }

  async changePogName(reqBody: ChangePogNameParams): Promise<void> {
    const response = await this.requestHandlerPost(API.changePogName, reqBody);

    return response.data.responseData;
  }

  async changePrivateAndSharedPogNames(reqBody: ChangePrivateAndSharedPogNamesParams): Promise<void> {
    const response = await this.requestHandlerPost(API.changePrivateAndSharedPogNames, reqBody);

    return response.data.responseData;
  }

  async updateSeedOwnership(reqBody: UpdateSeedOwnershipParams): Promise<void> {
    const response = await this.requestHandlerPost(API.updateSeedOwnership, reqBody);

    return response.data.responseData;
  }
}
